/**
 * Drip campaigns in the browser: the shapes the API returns, labels, and the email
 * preview (the same merge fields and paragraphs as utils/crmDrips.js on the server).
 * No imports and only erasable types, so node --test loads this file directly
 * (test/crm-drips.test.js).
 */

export type DripStatus = "draft" | "live" | "paused";
export type EnrollmentStatus = "active" | "finished" | "stopped" | "unsubscribed" | "failed";
/** "left": started the form, typed their email on its contact screen and left without sending it. */
export type FormOutcome = "any" | "qualified" | "not_fit" | "left";
export type DripTrigger = { type: "form"; form_id: number; outcome: FormOutcome } | { type: "stage"; stage: string };

export type DripStep = {
  id?: number | null;
  /** A key for React while the email has no id yet. */
  key?: string;
  wait_days: number;
  subject: string;
  body: string;
};

export type Drip = {
  id: number;
  name: string;
  status: DripStatus;
  from_name: string;
  /** The sender address before the @ on the No Limits domain; '' = the default (notifications). */
  from_local?: string;
  reply_to: string;
  footer_address: string;
  triggers: DripTrigger[];
  stop_on_booking: boolean;
  steps: DripStep[];
  updated_at: string;
  created_at: string;
  from_address?: string;
  sending?: { waiting_on_domain: boolean; message: string };
  /** The numbers across the top of the drip's page. */
  counts?: { people: number; active: number; finished: number; stopped: number; sent: number };
  /** People already in its start stages who are not in it yet: going live can start them. */
  in_stage_now?: number;
  /** After going live with include_current: how many of them started. */
  started?: number;
};

/** What a new drip starts with (the last drip's sender and address, else yours). */
export type DripDefaults = { from_name: string; reply_to: string; footer_address: string; from_local?: string; from_domain?: string; from_default_local?: string };

export type DripRow = {
  id: number;
  name: string;
  status: DripStatus;
  triggers: DripTrigger[];
  steps: number;
  active: number;
  finished: number;
  people: number;
  sent: number;
  sent_30_days: number;
  last_error: string | null;
  updated_at: string;
};

export type DripPerson = {
  id: number;
  contact_id: number;
  status: EnrollmentStatus;
  next_position: number;
  next_send_at: string | null;
  source: "manual" | "form" | "stage";
  stop_reason: string;
  last_error: string;
  created_at: string;
  first_name: string;
  last_name: string;
  email: string;
  company: string;
  sent: number;
};

export type ContactEnrollment = {
  id: number;
  drip_id: number;
  name: string;
  drip_status: DripStatus;
  status: EnrollmentStatus;
  next_position: number;
  next_send_at: string | null;
  stop_reason: string;
  last_error: string;
  steps: number;
};

export const DRIP_STATUS_LABEL: Record<DripStatus, string> = { draft: "Draft", live: "Live", paused: "Paused" };
export const ENROLLMENT_LABEL: Record<EnrollmentStatus, string> = {
  active: "Active", finished: "Finished", stopped: "Stopped", unsubscribed: "Unsubscribed", failed: "Failed",
};
export const OUTCOME_LABEL: Record<FormOutcome, string> = { any: "Any answer", qualified: "Qualified", not_fit: "Not a fit", left: "Left without sending it" };
export const MERGE_FIELDS = ["first_name", "last_name", "company", "email"] as const;
export const MAX_STEPS = 20;

/** The sample person the preview shows, and the same person with nothing filled in. */
export const SAMPLE_CONTACT = { first_name: "Jamie", last_name: "Rivera", company: "Rivera Roofing", email: "jamie@example.com" };
export const BLANK_CONTACT = { first_name: "", last_name: "", company: "", email: "" };

const MERGE = /\{\{\s*([a-zA-Z_]+)\s*(?:\|([^}]*))?\}\}/g;

/** "Hi {{first_name|there}}" → "Hi Jamie", or "Hi there" with no first name. Unknown fields are left empty. */
export function mergeFields(text: string, contact: Partial<Record<string, string>>): string {
  return String(text ?? "").replace(MERGE, (_m, name: string, fallback = "") => {
    const key = name.toLowerCase();
    const value = (MERGE_FIELDS as readonly string[]).includes(key) ? String(contact[key] ?? "").trim() : "";
    return value || String(fallback).trim();
  });
}

export type Segment = { text: string; href?: string };
const URL_IN_TEXT = /\b(?:https?:\/\/|www\.)[^\s<>"]+/gi;

/** One line as text and links, the way the email shows it. */
export function lineSegments(line: string): Segment[] {
  const out: Segment[] = [];
  let last = 0;
  for (const m of line.matchAll(URL_IN_TEXT)) {
    let url = m[0].replace(/[.,;:!?'"]+$/, "");
    if (url.endsWith(")") && !url.includes("(")) url = url.replace(/\)+$/, "");
    const at = m.index ?? 0;
    if (at > last) out.push({ text: line.slice(last, at) });
    out.push({ text: url, href: /^www\./i.test(url) ? `https://${url}` : url });
    last = at + url.length;
  }
  if (last < line.length) out.push({ text: line.slice(last) });
  return out;
}

/** Paragraphs (a blank line apart), each a list of lines, each a list of segments. */
export function previewParagraphs(text: string): Segment[][][] {
  return String(text ?? "").replace(/\r\n?/g, "\n").trim().split(/\n\s*\n/)
    .map((p) => p.trim()).filter(Boolean)
    .map((p) => p.split("\n").map(lineSegments));
}

/** When an email goes, in words: "2 days after they join", "Right after email 1". */
export function waitText(days: number, index: number): string {
  const n = Math.max(0, Math.round(Number(days) || 0));
  if (index === 0) return n === 0 ? "As soon as they join" : `${n} ${n === 1 ? "day" : "days"} after they join`;
  return n === 0 ? `Right after email ${index}` : `${n} ${n === 1 ? "day" : "days"} after email ${index}`;
}

/** What starts a drip, in a few words, for the list. */
export function triggerSummary(triggers: DripTrigger[], formName: (id: number) => string, stageName: (id: string) => string): string {
  if (!triggers.length) return "Added by hand";
  return triggers.map((t) => (t.type === "form"
    ? `${formName(t.form_id)}${t.outcome === "any" ? "" : t.outcome === "qualified" ? ", qualified" : t.outcome === "left" ? ", left without sending" : ", not a fit"}`
    : `Deal in ${stageName(t.stage)}`)).join("; ");
}

/** "2 of 5 sent". */
export const progressText = (sent: number, total: number) => `${Math.min(sent, total)} of ${total} sent`;

/** Who a drip starts for, as one sentence: "Starts when someone sends Casino survey and is not a fit." */
export function whoSentence(triggers: DripTrigger[], formName: (id: number) => string, stageName: (id: string) => string): string {
  if (!triggers.length) return "People you add by hand.";
  const parts = triggers.map((t) => (t.type === "form"
    ? t.outcome === "left" ? `someone leaves ${formName(t.form_id)} without sending it`
      : `someone sends ${formName(t.form_id)}${t.outcome === "qualified" ? " and qualifies" : t.outcome === "not_fit" ? " and is not a fit" : ""}`
    : `a deal moves to ${stageName(t.stage)}`));
  return `Starts when ${parts.join(" or ")}.`;
}

const FIELD_LABEL: Record<string, string> = { first_name: "First name", last_name: "Last name", company: "Company", email: "Email" };
/** "Hi {{first_name|there}}" → "Hi {First name}": an email's text as the team reads it in a list. */
export function readableFields(text: string): string {
  return String(text ?? "").replace(MERGE, (_m, name: string) => `{${FIELD_LABEL[name.toLowerCase()] || name}}`);
}
